import type { MaybeRefOrGetter } from 'vue'

// a person's detail opens in two ways. On the people list it is a route
// (`pages/people/[slug].vue`) that stacks on the list, so that the url can be
// shared and landed on. From any other page it opens as an overlay on top of
// that page instead (`site/person-overlay.vue`): the url stays put, the modal
// just opens and closes again.

// slug of the person currently open as an overlay, or null
export const usePersonOverlaySlug = () =>
  useState<string | null>('person-overlay-slug', () => null)

// whether a person link should open as an overlay from the current route
// rather than navigate: everywhere but the people list itself (and a person
// route already stacked on it, which navigates between people)
export const useOpensPersonInOverlay = () => {
  const route = useRoute()
  const getRouteBaseName = useRouteBaseName()

  return computed(() => {
    const name = String(getRouteBaseName(route) ?? '')
    return name !== 'people' && name !== 'people-slug'
  })
}

// the one implementation behind every link to a person (`shared/person-link.vue`,
// `site/payload-link.vue`): where the person opens as an overlay, the link is a
// plain anchor that keeps the route's href -- so a modified click (new tab, new
// window) still lands on the route version, the same clicks vue-router leaves
// to the browser -- and opens the overlay on a plain click
export const usePersonLink = (slug: MaybeRefOrGetter<string | null | undefined>) => {
  const localeRoute = useLocaleRoute()
  const overlaySlug = usePersonOverlaySlug()
  const opensInOverlay = useOpensPersonInOverlay()

  const routeObject = computed(() => ({ name: 'people-slug', params: { slug: toValue(slug) ?? '' } }))
  const href = computed(() => localeRoute(routeObject.value)?.href)

  const onClick = (event: MouseEvent) => {
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return
    if (event.button !== 0) return
    event.preventDefault()
    overlaySlug.value = toValue(slug) ?? null
  }

  return { opensInOverlay, routeObject, href, onClick }
}
